import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Modal, Form, Image, Row, Col, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import BookProgress from '../components/BookProgress';
import Message from '../components/Message';
import Loader from '../components/Loader';
import BookGuideModal from '../components/BookGuideModal';
import { getJobImages, getStoryContent, approvePages, regeneratePage, regenerateCover, getGenaiJobDetails } from '../actions/genaiActions';
import { GENAI_REGENERATE_PAGE_RESET, GENAI_REGENERATE_COVER_RESET } from '../constants/genaiConstants';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';
import { useTranslation } from 'react-i18next';

const TOTAL_REVIEW_PAGES = 14;
// Minimum images needed to show the gallery (back cover may be missing and need regeneration)
const MIN_GALLERY_PAGES = 13;

function PagesReviewScreen() {
    const { jobId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedPage, setSelectedPage] = useState(null);
    const [regenerationReason, setRegenerationReason] = useState('');
    const [pageStatuses, setPageStatuses] = useState({}); // Track approved/rejected pages
    const [regenCacheKeys, setRegenCacheKeys] = useState({}); // Cache-busting timestamps per page
    const [pendingRegenerationPage, setPendingRegenerationPage] = useState(null);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showMakeChangesConfirm, setShowMakeChangesConfirm] = useState(false);
    const [showGuideModal, setShowGuideModal] = useState(false);

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const genaiJobDetails = useSelector(state => state.genaiJobDetails);
    const { loading: loadingJob, error: errorJob, job } = genaiJobDetails;

    const genaiImages = useSelector(state => state.genaiImages);
    const { loading: loadingImages, error: errorImages, images } = genaiImages;

    const genaiStory = useSelector(state => state.genaiStory);
    const { pages: storyPages = [] } = genaiStory;

    // Get review pages (0-13, front cover + interior + back cover)
    // Declared here (above useEffects) to avoid TDZ error in dependency arrays
    const reviewPages = images ?
        Object.entries(images)
            .filter(([pageNum, _]) => Number(pageNum) >= 0 && Number(pageNum) <= 13)
            .sort(([a], [b]) => Number(a) - Number(b))
        : [];
    const hasAnyReviewPages = reviewPages.length > 0;
    const allReviewPagesReady = reviewPages.length === TOTAL_REVIEW_PAGES;
    const galleryReady = reviewPages.length >= MIN_GALLERY_PAGES;

    const genaiApprove = useSelector(state => state.genaiApprovePages);
    const { loading: loadingApprove, success: successApprove } = genaiApprove;

    const genaiRegenerate = useSelector(state => state.genaiRegeneratePage);
    const { loading: loadingRegenerate, success: successRegenerate } = genaiRegenerate;

    const genaiRegenerateCover = useSelector(state => state.genaiRegenerateCover);
    const { loading: loadingRegenerateCover, success: successRegenerateCover } = genaiRegenerateCover;

    const hasRegeneratingPage = Object.values(pageStatuses).includes('regenerating') || pendingRegenerationPage !== null;
    const interactionsLocked = loadingRegenerate || loadingRegenerateCover || job?.status === 'images_qa' || hasRegeneratingPage;
    const isTaletotsQa = job?.qa_mode === 'taletots';

    const storyTextByPage = useMemo(() => {
        const textMap = {};
        if (!Array.isArray(storyPages)) {
            return textMap;
        }

        storyPages.forEach((page) => {
            const pageKey = String(page?.page_number ?? '');
            if (!pageKey) {
                return;
            }
            const rawText = String(page?.text || page?.text_on_page || '').replace(/\*\*/g, '').trim();
            if (rawText) {
                textMap[pageKey] = rawText;
            }
        });

        return textMap;
    }, [storyPages]);

    useEffect(() => {
        if (!userInfo) {
            navigate('/login?redirect=/genai/create_story');
        } else {
            dispatch(getGenaiJobDetails(jobId));
            dispatch(getJobImages(jobId));
            dispatch(getStoryContent(jobId));
        }
    }, [dispatch, navigate, userInfo, jobId]);

    // Guard: redirect to final-review if book is already assembled and approved
    useEffect(() => {
        if (!job) return;
        // If the book is completed (PDF assembled) or assembly has started, redirect to final-review
        // (covers both customer approve-all and staff approve-all in taletots-QA mode)
        if (job.status === 'completed' || ['I_assemble', 'J_approve_final'].includes(job.current_step)) {
            navigate(`/genai/job/${jobId}/final-review`);
        }
    }, [job, jobId, navigate]);

    // Auto-advance legacy jobs stuck at F_approve_cover (cover review step removed from workflow)
    useEffect(() => {
        if (!job || job.current_step !== 'F_approve_cover' || !userInfo) return;
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        axios.post(`/api/genai/jobs/${jobId}/approve-cover/`, {}, config)
            .then(() => dispatch(getGenaiJobDetails(jobId)))
            .catch(() => {}); // silently ignore — job may have already advanced
    }, [job?.current_step, jobId, userInfo, dispatch]);

    // Poll for updates during generation/QA, and do a final fetch when done
    useEffect(() => {
        if (!job) return;
        const isGenerating = job.status === 'images_generating' || job.status === 'images_qa';
        // Keep polling until all 14 images are present (back cover may still be generating)
        const isDoneButIncomplete = job.status === 'awaiting_approval' && !allReviewPagesReady && !galleryReady;
        // taletots-QA jobs: keep polling while waiting on staff to approve the pages
        const waitingOnStaff = job.qa_mode === 'taletots' && job.current_step === 'H_approve_pages';

        if (isGenerating || isDoneButIncomplete || waitingOnStaff) {
            const interval = setInterval(() => {
                dispatch(getGenaiJobDetails(jobId));
                dispatch(getJobImages(jobId));
                dispatch(getStoryContent(jobId));
            }, 5000); // Poll every 5 seconds

            return () => clearInterval(interval);
        }
    }, [job?.status, job?.current_step, job?.qa_mode, allReviewPagesReady, dispatch, jobId]);

    useEffect(() => {
        if (successApprove) {
            // All pages approved, navigate to final review
            navigate(`/genai/job/${jobId}/final-review`);
        }
    }, [successApprove, navigate, jobId]);

    useEffect(() => {
        if (successRegenerate || successRegenerateCover) {
            // Capture page before clearing
            const regenPage = selectedPage;
            // Bust cache for this specific page so the browser fetches the new image
            setRegenCacheKeys(prev => ({ ...prev, [regenPage]: Date.now() }));
            // Keep the page locked until the refreshed gallery data comes back.
            setPendingRegenerationPage(regenPage);
            // Page/cover regenerated, reload the images
            dispatch(getJobImages(jobId));
            setShowRejectModal(false);
            setRegenerationReason('');
            setSelectedPage(null);
            // Reset so subsequent rejects work correctly
            dispatch({ type: GENAI_REGENERATE_PAGE_RESET });
            dispatch({ type: GENAI_REGENERATE_COVER_RESET });
        }
    }, [successRegenerate, successRegenerateCover, dispatch, jobId, selectedPage]);

    useEffect(() => {
        if (pendingRegenerationPage !== null && !loadingImages) {
            setPageStatuses(prev => {
                const newStatuses = { ...prev };
                delete newStatuses[pendingRegenerationPage];
                return newStatuses;
            });
            setPendingRegenerationPage(null);
        }
    }, [loadingImages, pendingRegenerationPage]);

    const handleRejectPage = (pageNum) => {
        if (interactionsLocked) return;
        setPageStatuses(prev => ({ ...prev, [pageNum]: 'rejected' }));
        setSelectedPage(pageNum);
        setShowRejectModal(true);
    };

    const handleApprovePage = (pageNum) => {
        if (interactionsLocked) return;
        setPageStatuses(prev => ({ ...prev, [pageNum]: 'approved' }));
    };

    const handleSubmitRegeneration = () => {
        if (interactionsLocked || selectedPage === null) {
            return;
        }
        if (!regenerationReason.trim()) {
            alert(t('pagesReview.alertNoReason'));
            return;
        }
        // Mark this page as regenerating immediately so the old image is hidden
        setPageStatuses(prev => ({ ...prev, [selectedPage]: 'regenerating' }));
        setShowRejectModal(false);
        setRegenerationReason('');
        if (selectedPage === '0') {
            dispatch(regenerateCover(jobId, regenerationReason));
        } else {
            dispatch(regeneratePage(jobId, selectedPage, regenerationReason));
        }
    };

    const handleApproveAll = () => {
        if (interactionsLocked) return;
        // Check if any pages are rejected
        const rejectedPages = Object.entries(pageStatuses)
            .filter(([_, status]) => status === 'rejected')
            .map(([page, _]) => page);
        
        if (rejectedPages.length > 0) {
            alert(t('pagesReview.alertRegenFirst', { list: rejectedPages.join(', ') }));
            return;
        }

        setShowApproveConfirm(true);
    };

    const handleConfirmApproveAll = () => {
        setShowApproveConfirm(false);
        dispatch(approvePages(jobId));
    };

    const handleMakeChanges = () => {
        setShowMakeChangesConfirm(true);
    };

    const handleConfirmMakeChanges = () => {
        setShowMakeChangesConfirm(false);
        navigate(`/genai/job/${jobId}/review-story`);
    };

    const getPageStatus = (pageNum) => {
        return pageStatuses[pageNum] || 'pending';
    };

    return (
        <div>
            <BookProgress step1 step2 step3 jobId={jobId} />

            <h1>{t('pagesReview.title')}</h1>
            <p className="text-muted">
                {t('pagesReview.subtitle')}
            </p>

            {loadingJob || loadingImages ? (
                <Loader />
            ) : errorJob ? (
                <Message variant='danger'>{errorJob}</Message>
            ) : errorImages ? (
                <Message variant='danger'>{errorImages}</Message>
            ) : (
                <>
                    {job && (
                        <Alert 
                            variant={job.status === 'images_qa' ? 'warning' : 'info'} 
                            className="mb-4"
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{t('pagesReview.jobStatus')}</strong>{' '}
                                    {job.status === 'images_qa' ? (
                                        <span> {t('pagesReview.regeneratingPage')}</span>
                                    ) : job.status === 'assembling' ? (
                                        <span> {t('pagesReview.assembling')}</span>
                                    ) : (
                                        <span> {job.status}</span>
                                    )}
                                    {' | '}
                                    <strong>{t('pagesReview.currentStep')}</strong> {t('pagesReview.pagesReview')}
                                </div>
                                <div className="text-end">
                                    <small className="text-muted">
                                        {t('pagesReview.regenerations', { count: job.user_regeneration_count || 0 })}
                                    </small>
                                </div>
                            </div>
                            {(job.status === 'images_qa' || job.status === 'assembling' || (job.status === 'awaiting_approval' && !allReviewPagesReady)) && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        {t('pagesReview.autoRefresh')}
                                    </small>
                                </div>
                            )}
                        </Alert>
                    )}

                    <Alert variant="info" className="mb-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span>
                                <strong>Layout note:</strong> The final printed book uses separate text pages. You are approving illustrations here; story text is assembled on dedicated pages.
                            </span>
                            <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => setShowGuideModal(true)}
                            >
                                {t('common.viewBookLayoutGuide')}
                            </Button>
                        </div>
                    </Alert>

                    <BookGuideModal show={showGuideModal} onHide={() => setShowGuideModal(false)} />

                    {/* Page Status Summary — shown whenever at least one page is available */}
                    {job?.status === 'awaiting_approval' && hasAnyReviewPages && !isTaletotsQa && (
                    <Card className="mb-4 bg-light">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">{t('pagesReview.reviewProgress')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('pagesReview.approved')} <strong>{Object.values(pageStatuses).filter(s => s === 'approved').length}</strong> / 
                                        {t('pagesReview.rejected')} <strong>{Object.values(pageStatuses).filter(s => s === 'rejected').length}</strong> / 
                                        {t('pagesReview.pendingLabel')} <strong>{reviewPages.length - Object.keys(pageStatuses).length}</strong>
                                    </p>
                                </div>
                            </div>
                            {!allReviewPagesReady && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        {t('pagesReview.autoRefresh')}
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                    )}

                    {/* Pages Gallery — show any available pages so partial refreshes do not hide the whole review. */}
                    {isTaletotsQa && job?.status === 'awaiting_approval' && hasAnyReviewPages ? (
                        <div className="text-center py-4">
                            <Alert variant="info" className="mb-3">
                                {t('pagesReview.staffReviewingPages')}
                            </Alert>
                            {(() => {
                                const frontCover = reviewPages.find(([pageNum]) => pageNum === '0');
                                if (!frontCover) return <Loader />;
                                const [pageNum, image] = frontCover;
                                return (
                                    <div style={getImageFrameStyle(image, pageNum, { maxHeightCover: 320, maxHeightStory: 320 })}>
                                        <Image
                                            src={image.url || image.image_url}
                                            alt={t('pagesReview.frontCover')}
                                            fluid
                                            className="border rounded"
                                            style={getImageTagStyle()}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    ) : job?.status === 'awaiting_approval' && hasAnyReviewPages ? (
                        <Row className="mb-4">
                            {reviewPages.map(([pageNum, image]) => {
                                const status = getPageStatus(pageNum);
                                return (
                                    <Col key={pageNum} md={6} lg={4} className="mb-4">
                                        <Card className={`h-100 shadow-sm ${
                                            status === 'approved' ? 'border-success' : 
                                            status === 'rejected' ? 'border-danger' : 
                                            status === 'regenerating' ? 'border-warning' :
                                            'border-secondary'
                                        }`} style={{ borderWidth: '2px' }}>
                                            <Card.Header className="d-flex justify-content-between align-items-center">
                                                <strong>{pageNum === '0' ? t('pagesReview.frontCover') : pageNum === '13' ? t('pagesReview.backCover') : t('pagesReview.pageN', { n: pageNum })}</strong>
                                                {status === 'approved' && <Badge bg="success">{t('pagesReview.badgeApproved')}</Badge>}
                                                {status === 'rejected' && <Badge bg="danger">{t('pagesReview.badgeRejected')}</Badge>}
                                                {status === 'regenerating' && <Badge bg="warning" text="dark">{t('pagesReview.badgeRegenerating')}</Badge>}
                                                {status === 'pending' && <Badge bg="secondary">{t('pagesReview.badgePending')}</Badge>}
                                            </Card.Header>
                                            <Card.Body className="p-2">
                                                <div className="text-center mb-2">
                                                    {status === 'regenerating' ? (
                                                        <div
                                                            className="border rounded d-flex flex-column align-items-center justify-content-center bg-light"
                                                            style={{ height: '220px', width: '100%' }}
                                                        >
                                                            <div className="spinner-border text-warning mb-2" role="status">
                                                                <span className="visually-hidden">{t('pagesReview.regeneratingAlt')}</span>
                                                            </div>
                                                            <small className="text-muted">{t('pagesReview.generatingIllustration')}</small>
                                                            <small className="text-muted">{t('pagesReview.generatingWait')}</small>
                                                        </div>
                                                    ) : (
                                                        <div style={getImageFrameStyle(image, pageNum, { maxHeightCover: 300, maxHeightStory: 300 })}>
                                                            <Image 
                                                                src={`${image.url || image.image_url}${regenCacheKeys[pageNum] ? '?cb=' + regenCacheKeys[pageNum] : ''}`} 
                                                                alt={pageNum === '0' ? t('pagesReview.frontCover') : pageNum === '13' ? t('pagesReview.backCover') : t('pagesReview.pageN', { n: pageNum })}
                                                                fluid
                                                                className="border rounded"
                                                                style={{
                                                                    ...getImageTagStyle(),
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => window.open(image.url || image.image_url, '_blank')}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="mb-3 px-1">
                                                    <div className="text-uppercase fw-semibold text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                                        {t('pagesReview.textPreviewLabel')}
                                                    </div>
                                                    <p className="mb-0 small" style={{ lineHeight: '1.4', maxHeight: '92px', overflowY: 'auto' }}>
                                                        {storyTextByPage[pageNum] || <em>{t('pagesReview.noTextPreview')}</em>}
                                                    </p>
                                                </div>
                                                <div className="d-flex gap-2 justify-content-center">
                                                    {status !== 'approved' && (
                                                        <Button 
                                                            variant="success" 
                                                            size="sm"
                                                            onClick={() => handleApprovePage(pageNum)}
                                                            disabled={interactionsLocked}
                                                        >
                                                            {t('pagesReview.approveBtn')}
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="danger" 
                                                        size="sm"
                                                        onClick={() => handleRejectPage(pageNum)}
                                                        disabled={interactionsLocked || status === 'regenerating'}
                                                    >
                                                        {t('pagesReview.rejectBtn')}
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <div className="text-center py-4">
                            <h4 style={{ fontFamily: 'Georgia, serif', color: '#5a3e28' }}>{t('pagesReview.bookBeingMade')}</h4>
                            <p className="text-muted mb-1">{t('pagesReview.bookBeingMadeDesc')}</p>
                            <p className="text-muted mb-1">{t('pagesReview.bookBeingMadeWait')}</p>
                            <p className="text-muted mb-3">{t('pagesReview.bookBeingMadeClose')}</p>
                            <img
                                src="/site-assets/family-story-spread.jpg"
                                alt={t('pagesReview.bookBeingMadeAlt')}
                                style={{ maxWidth: '320px', width: '100%', borderRadius: '12px' }}
                            />
                        </div>
                    )}

                    {/* Action Buttons — only shown when the customer is the one reviewing */}
                    {!isTaletotsQa && (
                        <Card className="mb-4 p-3 bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5>{t('pagesReview.allReviewed')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('pagesReview.allReviewedHint')}
                                    </p>
                                </div>
                                <Button 
                                    variant="success" 
                                    size="lg"
                                    onClick={handleApproveAll}
                                    disabled={loadingApprove || interactionsLocked || !allReviewPagesReady}
                                >
                                    {loadingApprove ? t('pagesReview.approvingAll') : t('pagesReview.approveAll')}
                                </Button>
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* Rejection Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('pagesReview.feedbackTitle', { page: selectedPage === '0' ? t('pagesReview.frontCover') : selectedPage === '13' ? t('pagesReview.backCover') : t('pagesReview.pageN', { n: selectedPage }) })}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        {t('pagesReview.feedbackHint')}
                    </Alert>
                    <Form.Group controlId="regenerationReason">
                        <Form.Label>{t('pagesReview.feedbackLabel')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder={t('pagesReview.feedbackPlaceholder')}
                            value={regenerationReason}
                            onChange={(e) => setRegenerationReason(e.target.value)}
                            maxLength={1000}
                        />
                        <Form.Text className="text-muted">
                            {t('pagesReview.charCount', { count: regenerationReason.length })}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)} disabled={interactionsLocked}>
                        {t('pagesReview.cancel')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmitRegeneration}
                        disabled={interactionsLocked || !regenerationReason.trim()}
                    >
                        {(loadingRegenerate || loadingRegenerateCover) ? t('pagesReview.regeneratingBtn') : t('pagesReview.regenerateBtn', { page: selectedPage === '0' ? t('pagesReview.frontCover') : selectedPage === '13' ? t('pagesReview.backCover') : t('pagesReview.pageN', { n: selectedPage }) })}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Approve All confirmation modal */}
            <Modal show={showApproveConfirm} onHide={() => setShowApproveConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('pagesReview.confirmTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('pagesReview.confirmBody')}</p>
                    <p className="text-muted mb-0">{t('pagesReview.confirmNote')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApproveConfirm(false)}>
                        {t('pagesReview.confirmBack')}
                    </Button>
                    <Button variant="success" onClick={handleConfirmApproveAll}>
                        {t('pagesReview.confirmApprove')}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Make Changes Warning Modal */}
            <Modal show={showMakeChangesConfirm} onHide={() => setShowMakeChangesConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('pagesReview.makeChangesTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        <strong>{t('pagesReview.makeChangesWarning')}</strong>
                    </Alert>
                    <p>{t('pagesReview.makeChangesDesc')}</p>
                    <p className="text-muted mb-0">{t('pagesReview.makeChangesConfirm')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMakeChangesConfirm(false)}>
                        {t('pagesReview.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleConfirmMakeChanges}>
                        {t('pagesReview.makeChangesConfirmBtn')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Make Changes Button at Bottom (hide while book is still being created) */}
            {job?.status === 'awaiting_approval' && hasAnyReviewPages && (
                <div className="mt-5 pt-4 border-top">
                    <Card className="p-4 bg-light border-warning" style={{ borderWidth: '2px' }}>
                        <div>
                            <h5 className="mb-2 text-warning">{t('pagesReview.notHappyTitle')}</h5>
                            <p className="mb-3 text-muted">{t('pagesReview.notHappyDesc')}</p>
                            <Button 
                                variant="outline-warning" 
                                size="lg"
                                onClick={handleMakeChanges}
                                disabled={interactionsLocked}
                                className="w-100"
                            >
                                {t('pagesReview.makeChangesBtn')}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

export default PagesReviewScreen;
